import { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/features/auth/contexts/AuthContext";
import { USE_MOCK } from "@/api/client";
import { trackPageView, trackScreenTime } from "@/shared/lib/posthog";
import { attributionParams } from "@/shared/lib/attribution";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { useUserSocket } from "@/features/meetings/hooks/use-user-socket";
import { useNotificationsStream } from "@/features/notifications/hooks/use-notifications-stream";
import { useMeetingReminders } from "@/features/notifications/hooks/use-meeting-reminders";
import LoginRedesign from "./pages/LoginRedesign";
import DesktopAuth from "./pages/DesktopAuth";
import AuthMobile from "./pages/AuthMobile";
import { PENDING_MOBILE_HANDOFF_KEY } from "@/shared/lib/mobile-handoff";
import { lazyWithRetry } from "@/shared/lib/lazy-with-retry";
import { rememberSurface, surfaceHint, useCaptureMe, wantsFloorApp } from "./features/capture/use-capture-me";
import { isGtmHost } from "./internal/gtm/host-mode";
import { Spinner } from "@/components/ui/spinner";

// The entire operator workbench (Shell + chat; each page is a further chunk)
// loads through React.lazy so it is a separate chunk, fetched only when a
// workbench user is actually routed into it. This is what lets one binary
// serve both audiences: a WF10 capture-only user boots, logs in, and renders
// the capture surface without ever downloading the desktop app. Keep every
// workbench import inside AppRoutes.tsx, not here. lazyWithRetry: after a
// deploy an open tab's old AppRoutes hash 404s; it reloads once instead.
const AppRoutes = lazyWithRetry(() => import("./AppRoutes"));

// The WF10 floor app — the entire app for a user whose app_surface is
// "capture". Its own small chunk; neither audience downloads the other's app.
const CaptureApp = lazy(() => import("./pages/capture/CaptureApp"));

// The GTM portal — the entire app on gtm.theubik.com (internal/gtm/host-mode).
// Same build and service as the workbench; on that host it replaces AppRoutes,
// so a founder there never downloads the Shell or chat.
const GtmApp = lazyWithRetry(() => import("./internal/gtm/pages/GtmApp"));

// Phone + OTP sign-in for floor staff with no email address. LAZY, and that
// is load-bearing: it is the only route allowed to reach libphonenumber's
// metadata, and LoginRedesign (the sibling login screen) is a STATIC import
// below — so if this were static too, every phone would download the phone
// metadata before it could render anything. e2e/smoke.spec.ts asserts the
// libphonenumber chunk stays off the boot graph.
const PhoneLogin = lazy(() => import("./pages/PhoneLogin"));

// Public / rarely-hit routes: lazy for the same reason. ArtifactShare pulls
// in every artifact renderer, which would otherwise anchor the entry chunk.
const ShareThread = lazy(() => import("./pages/ShareThread"));
const ArtifactShare = lazy(() => import("./pages/ArtifactShare"));
const ArtifactEmbed = lazy(() => import("./pages/ArtifactEmbed"));
const PlanningReportShare = lazy(() => import("./pages/PlanningReportShare"));
// Logistics driver form (L5): a signed link, no login, outside the authenticated shell.
const DriverFormPage = lazy(() =>
  import("./features/erp-flows/modules/log/screens/Driver").then((m) => ({ default: m.DriverFormPage })),
);
const ComposioConnected = lazy(() => import("./pages/ComposioConnected"));

// `onRetry` is what stops this being an infinite spinner: the capture branch in
// SurfaceGate parks here when /capture/me is unreachable, and on a factory floor
// that can last a whole shift with nothing on screen to press.
function RouteFallback({
  onRetry,
  message = "Loading...",
}: {
  onRetry?: () => void;
  message?: string;
}) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        {!onRetry && (
          <Spinner className="size-8 text-muted-foreground" />
        )}
        <p className="text-sm text-muted-foreground">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    const enteredAt = Date.now();
    const pageName = location.pathname;
    trackPageView(pageName);
    return () => {
      const seconds = Math.round((Date.now() - enteredAt) / 1000);
      if (seconds > 1) trackScreenTime(pageName, seconds);
    };
  }, [location.pathname]);
  return null;
}

// Handles /login for already-authenticated users: redirect to the desktop or
// mobile handoff when ?from= is present, otherwise to /.
function LoginRoute({ authData }: { authData: ReturnType<typeof useAuth>["authData"] }) {
  const [searchParams] = useSearchParams();
  if (authData) {
    const from = searchParams.get("from");
    if (from === "desktop") return <Navigate to="/auth/desktop" replace />;
    if (from === "mobile") return <Navigate to="/auth/mobile" replace />;
    return <Navigate to="/" replace />;
  }
  return <LoginRedesign />;
}

/** The retry on the unreachable-session screen.
 *
 *  Its own component for one reason: a button that does nothing visible when
 *  the retry ALSO fails reads as a broken button, and the operator taps it
 *  repeatedly. Local state, so a failed retry still shows that something
 *  happened without dragging the whole app back to a spinner. */
function RetrySessionButton({ onRetry }: { onRetry: () => Promise<void> }) {
  const [checking, setChecking] = useState(false);
  return (
    <button
      type="button"
      disabled={checking}
      onClick={async () => {
        setChecking(true);
        try {
          await onRetry();
        } finally {
          setChecking(false);
        }
      }}
      className="mt-2 min-h-[48px] rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60"
    >
      {checking ? "Checking…" : "Try again"}
    </button>
  );
}

function AuthenticatedApp() {
  const { authData, loading, sessionUnreachable, refreshSession } = useAuth();
  const navigate = useNavigate();

  // Subscribe to the user's lifecycle channel so the meetings list updates
  // instantly when the desktop creates or ends a session. The hook no-ops
  // while userId is null (during loading or post-logout).
  useUserSocket(authData?.userProfile.id ?? null);

  // Subscribe to the per-user notification SSE stream: pushes new notifications
  // to the bell, plays the sound, and toasts (no-op while unauthenticated).
  useNotificationsStream(authData?.userProfile.id ?? null);

  // Client-side 5-min-before meeting reminders (calendar is only available FE-side).
  useMeetingReminders(authData?.userProfile.id ?? null);

  // After a fresh sign-in, the OAuth callback drops the user at `/?session=...`
  // (handled by AuthContext). If they originally came from the desktop handoff
  // page, send them straight back so the deep link / loopback delivery fires.
  useEffect(() => {
    if (loading || !authData) return;
    if (sessionStorage.getItem("ubik_desktop_handoff_pending")) {
      sessionStorage.removeItem("ubik_desktop_handoff_pending");
      navigate("/auth/desktop", { replace: true });
      return;
    }
    // Same trick for the Android app's Custom-Tab login (see AuthMobile).
    if (sessionStorage.getItem(PENDING_MOBILE_HANDOFF_KEY)) {
      sessionStorage.removeItem(PENDING_MOBILE_HANDOFF_KEY);
      navigate("/auth/mobile", { replace: true });
    }
  }, [authData, loading, navigate]);

  if (loading) {
    return <RouteFallback />;
  }

  // A stored token the backend could not be reached to confirm. Showing the
  // login form here is the bug it replaces: a signed-in operator is asked to
  // sign in again, does, and mints a second session — observed on a floor
  // handset that accumulated three. Offer the retry instead; the session is
  // almost certainly fine and the building's signal is not.
  if (!authData && sessionUnreachable) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-base font-semibold">Can't reach Ubik</p>
        <p className="max-w-xs text-sm text-muted-foreground">
          You're still signed in — we just couldn't confirm it. Check your
          signal and try again.
        </p>
        <RetrySessionButton onRetry={refreshSession} />
      </div>
    );
  }

  return (
    <Routes>
      {/* Desktop handoff page handles its own auth gating (stashes a flag and
          redirects to /login when unauthenticated). Listed above the wildcard
          so it isn't caught by the auth-required catch-all. */}
      <Route path="/auth/desktop" element={<DesktopAuth />} />
      {/* The Android app's handoff — same contract, custom-scheme transport. */}
      <Route path="/auth/mobile" element={<AuthMobile />} />
      <Route path="/login" element={<LoginRoute authData={authData} />} />
      {/* Phone + OTP. Lives INSIDE AuthenticatedApp, not in the public block
          at the bottom of this file, because it calls requestOtp/verifyOtp on
          the AuthContext — which is only in scope under <AuthProvider>. An
          already-signed-in operator is bounced home, exactly like /login. */}
      <Route
        path="/login/phone"
        element={authData ? <Navigate to="/" replace /> : <PhoneLogin />}
      />
      {/* The host decides the tree before the surface does: gtm.theubik.com
          renders only the GTM portal. Login above is shared, and the OAuth
          callback lands on this origin's `/?session=` as everywhere else. */}
      <Route
        path="/*"
        element={authData ? (isGtmHost() ? <GtmApp /> : <SurfaceGate />) : <LoginRedirect />}
      />
    </Routes>
  );
}

/** Signed-out visitors land on /login. Carry over only the marketing-site
 *  attribution params (UTMs, landing page) so sign-in can pass them to the
 *  auth service; every other param is dropped as before. */
function LoginRedirect() {
  const location = useLocation();
  const params = attributionParams(location.search).toString();
  return <Navigate to={params ? `/login?${params}` : "/login"} replace />;
}

/**
 * Which app does this person get? Backend-authoritative via
 * GET /api/v1/capture/me (`app_surface`), with three deliberate properties:
 *  - absent field, request error, or mock mode → "full", so a frontend
 *    deployed ahead of the backend changes nothing for anyone;
 *  - a localStorage hint avoids flashing (and downloading) the workbench on a
 *    capture-only phone while /me is in flight — a first-paint hint only, the
 *    response always wins;
 *  - the gate only picks a TREE. Data access is enforced per-request by every
 *    workflow's own backend regardless of what renders here.
 */
function SurfaceGate() {
  const { data: me, isPending, isError, refetch } = useCaptureMe(true);

  useEffect(() => {
    rememberSurface(me);
  }, [me]);

  if (me) {
    return wantsFloorApp(me) ? <CaptureApp /> : <AppRoutes />;
  }
  // The hint has to hold on `isError` too, not just while in flight. A capture
  // phone that boots offline errors here, and falling through to <AppRoutes />
  // asks for the workbench chunk, which cannot be fetched with no network —
  // white screen on the one device that is meant to work offline.
  // The hint only decides a FIRST PAINT, so it has to agree with the gate
  // above: on a laptop, a capture account belongs in the workbench, and
  // holding that paint back would be holding back the right answer.
  if (!USE_MOCK && surfaceHint() === "capture" && wantsFloorApp({ enabled: true, app_surface: "capture" })) {
    if (isPending) return <RouteFallback />;
    if (isError) {
      return (
        <RouteFallback
          message="Can't reach Ubik. Check your connection."
          onRetry={() => void refetch()}
        />
      );
    }
  }
  return <AppRoutes />;
}

const App = () => (
  <TooltipProvider>
    <Sonner />
    <CookieConsentBanner />
    <BrowserRouter>
      <RouteChangeTracker />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/share/:threadId" element={<ShareThread />} />
          <Route path="/share/artifact/:token" element={<ArtifactShare />} />
          <Route path="/embed/artifact/:token" element={<ArtifactEmbed />} />
          <Route path="/planning/report/:token" element={<PlanningReportShare />} />
          <Route path="/driver/:token" element={<DriverFormPage />} />
          {/* Composio popup-connect success — minimal self-closing page, no chrome */}
          <Route path="/integrations/connected" element={<ComposioConnected />} />
          {/* Sheet Capture's old addresses.
              These four shipped as their own sidebar entries and are live on
              staging, so bookmarks exist. They redirect HERE, above <Shell>,
              and not from inside AppRoutes — a redirect declared in there is
              undone: Shell's URL-to-tab effect is the PARENT, so it runs after
              the child <Navigate> fires, re-navigates to the path it captured,
              and mints a chat tab. `/intelligence` and `/admin/users` are both
              broken that way today. Resolving above the Shell sidesteps it
              entirely; a redirect needs no auth, and /capture then goes through
              the normal flow. */}
          <Route path="/documents" element={<Navigate to="/capture" replace />} />
          <Route path="/documents/figures" element={<Navigate to="/capture" replace />} />
          <Route path="/documents/:day" element={<Navigate to="/capture" replace />} />
          <Route path="/document-setup" element={<Navigate to="/capture" replace />} />
          {/* All other routes */}
          <Route
            path="/*"
            element={
              USE_MOCK ? (
                <AppRoutes />
              ) : (
                <AuthProvider>
                  <AuthenticatedApp />
                </AuthProvider>
              )
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
